import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Typography,
  Box,
  Paper,
  Button,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  Divider,
  Alert,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  CircularProgress,
  LinearProgress
} from '@mui/material';
import DirectionsCarIcon from '@mui/icons-material/DirectionsCar';
import AddCircleIcon from '@mui/icons-material/AddCircle';
import BlockIcon from '@mui/icons-material/Block';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useAuth } from '../../../contexts/AuthContext';
import { ImportResult } from '../../../types/fuelImport';

/**
 * Page de gestion des véhicules non reconnus pour l'importation de carburant
 */
const FuelUnknownVehiclesPage: React.FC = (): React.ReactElement => {
  const navigate = useNavigate();
  // Nous utiliserons l'authentification plus tard si nécessaire
  const auth = useAuth();
  
  const [unknownVehicles, setUnknownVehicles] = useState<Array<{
    matricule: string;
    count: number;
  }>>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [processing, setProcessing] = useState<boolean>(false);
  const [currentVehicle, setCurrentVehicle] = useState<string | null>(null);
  const [openDialog, setOpenDialog] = useState<boolean>(false);
  const [progress, setProgress] = useState<{
    total: number;
    created: number;
    ignored: number;
  }>({ total: 0, created: 0, ignored: 0 });
  const [error, setError] = useState<string | null>(null);
  
  // Charger les données du localStorage au chargement de la page
  useEffect(() => {
    const storedResult = localStorage.getItem('fuelImport_result');
    
    if (!storedResult) {
      // Rediriger vers la page d'importation si les données ne sont pas disponibles
      navigate('/admin/fuel/import');
      return;
    }
    
    try {
      const result = JSON.parse(storedResult) as ImportResult;
      setUnknownVehicles(result.unknownVehicles);
      setProgress({
        total: result.unknownVehicles.length,
        created: 0,
        ignored: 0
      });
      setLoading(false);
    } catch (error) {
      console.error('Erreur lors du chargement des résultats:', error);
      setError('Impossible de charger les véhicules non reconnus. Veuillez réessayer.');
      setLoading(false);
    }
  }, [navigate]);
  
  // Gérer le clic sur le bouton "Créer ce véhicule"
  const handleCreateVehicle = (matricule: string) => {
    setCurrentVehicle(matricule);
    setOpenDialog(true);
  };
  
  // Gérer la confirmation de création de véhicule
  const handleConfirmCreate = () => {
    setOpenDialog(false);
    
    if (!currentVehicle) return;
    
    // Simuler la création d'un véhicule
    setProcessing(true);
    
    setTimeout(() => {
      // Mettre à jour la liste des véhicules non reconnus
      setUnknownVehicles(prev => prev.filter(v => v.matricule !== currentVehicle));
      
      // Mettre à jour la progression
      setProgress(prev => ({
        ...prev,
        created: prev.created + 1
      }));
      
      setProcessing(false);
      setCurrentVehicle(null);
      
      // Rediriger vers la page de création de véhicule (à implémenter)
      // navigate(`/vehicle/create?matricule=${currentVehicle}`);
      
      // Pour l'instant, nous simulons simplement la création
      console.log(`Création du véhicule ${currentVehicle}`);
    }, 1000);
  };
  
  // Gérer l'annulation de création de véhicule
  const handleCancelCreate = () => {
    setOpenDialog(false);
    setCurrentVehicle(null);
  };
  
  // Gérer le clic sur le bouton "Ignorer"
  const handleIgnoreVehicle = (matricule: string) => {
    // Mettre à jour la liste des véhicules non reconnus
    setUnknownVehicles(prev => prev.filter(v => v.matricule !== matricule));
    
    // Mettre à jour la progression
    setProgress(prev => ({
      ...prev,
      ignored: prev.ignored + 1
    }));
  };
  
  // Gérer le clic sur le bouton "Retour"
  const handleBack = () => {
    navigate('/admin/fuel/import/results');
  };
  
  // Gérer le clic sur le bouton "Terminer"
  const handleFinish = () => {
    navigate('/admin/fuel/import/results');
  };
  
  return (
    <Container maxWidth="lg">
      <Box sx={{ my: 4 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
          <IconButton
            onClick={handleBack}
            sx={{ mr: 2 }}
            aria-label="retour"
          >
            <ArrowBackIcon />
          </IconButton>
          <Typography variant="h4" component="h1">
            Véhicules non reconnus
          </Typography>
        </Box>
        
        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}
        
        <Paper sx={{ p: 3, mb: 3 }}>
          <Typography variant="h6" gutterBottom>
            Progression
          </Typography>
          
          <Box sx={{ mb: 2 }}>
            <LinearProgress 
              variant="determinate" 
              value={((progress.created + progress.ignored) / progress.total) * 100} 
              sx={{ height: 10, borderRadius: 5 }}
            />
          </Box>
          
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
            <Typography variant="body2" color="text.secondary">
              {progress.created + progress.ignored} sur {progress.total} véhicules traités
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {Math.round(((progress.created + progress.ignored) / progress.total) * 100)}%
            </Typography>
          </Box>
          
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
            <Chip 
              icon={<DirectionsCarIcon />} 
              label={`${progress.total} véhicules non reconnus`} 
              color="default" 
              variant="outlined" 
            />
            <Chip 
              icon={<AddCircleIcon />} 
              label={`${progress.created} véhicules créés`} 
              color="success" 
              variant="outlined" 
            />
            <Chip 
              icon={<BlockIcon />} 
              label={`${progress.ignored} véhicules ignorés`} 
              color="error" 
              variant="outlined" 
            />
          </Box>
        </Paper>
        
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
            <CircularProgress />
          </Box>
        ) : unknownVehicles.length === 0 ? (
          <Alert severity="success" sx={{ mb: 3 }}>
            Tous les véhicules ont été traités.
          </Alert>
        ) : (
          <Paper sx={{ mb: 3 }}>
            <List>
              {unknownVehicles.map((vehicle, index) => (
                <React.Fragment key={vehicle.matricule}>
                  <ListItem>
                    <ListItemIcon>
                      <DirectionsCarIcon color="warning" />
                    </ListItemIcon>
                    <ListItemText
                      primary={vehicle.matricule}
                      secondary={`${vehicle.count} transaction${vehicle.count > 1 ? 's' : ''}`}
                    />
                    <ListItemSecondaryAction>
                      <Button
                        variant="outlined"
                        color="error"
                        size="small"
                        onClick={() => handleIgnoreVehicle(vehicle.matricule)}
                        disabled={processing}
                        sx={{ mr: 1 }}
                      >
                        Ignorer
                      </Button>
                      <Button
                        variant="contained"
                        color="primary"
                        size="small"
                        onClick={() => handleCreateVehicle(vehicle.matricule)}
                        disabled={processing}
                        startIcon={processing && currentVehicle === vehicle.matricule ? <CircularProgress size={20} /> : undefined}
                      >
                        Créer ce véhicule
                      </Button>
                    </ListItemSecondaryAction>
                  </ListItem>
                  {index < unknownVehicles.length - 1 && <Divider />}
                </React.Fragment>
              ))}
            </List>
          </Paper>
        )}
        
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 3 }}>
          <Button
            variant="outlined"
            onClick={handleBack}
            startIcon={<ArrowBackIcon />}
          >
            Retour aux résultats
          </Button>
          
          <Button
            variant="contained"
            onClick={handleFinish}
            disabled={processing}
          >
            Terminer
          </Button>
        </Box>
      </Box>
      
      {/* Dialogue de confirmation de création de véhicule */}
      <Dialog
        open={openDialog}
        onClose={handleCancelCreate}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle id="alert-dialog-title">
          Créer un nouveau véhicule
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            Vous allez être redirigé vers le formulaire de création de véhicule pour la plaque d'immatriculation <strong>{currentVehicle}</strong>.
            Après la création, les transactions associées à ce véhicule seront traitées automatiquement.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelCreate} color="inherit">
            Annuler
          </Button>
          <Button onClick={handleConfirmCreate} color="primary" autoFocus>
            Continuer
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default FuelUnknownVehiclesPage;